/**
 * ファイル関連処理
 */
import {
  GET_DEVICES_ACTIVATION_OUTPUT_ID,
  setApiErrorMessage
} from 'common/modules/domain/deviceActivate';
import {
  ActivationData,
  ActivationHeader
} from 'common/utils/request/sas-response';
import JSZip from 'jszip';
import { BoardGL, Glossary } from './glossary';
import {
  BoardStatus,
  setIsProcessing,
  setStatus
} from 'common/modules/app/board';
import { ThunkDispatch } from 'redux-thunk';
import { ErrorResponse, fetchFailure } from 'common/modules/app/errors';
import { failedFetch } from 'common/modules/app/fetch';

export const outputACTFile = async (
  actHeader: ActivationHeader,
  vendorPurchaseId: string,
  dataList: ActivationData[],
  dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
  glossary: Glossary
) => {
  // 200件ずつに分割する
  function chunk<T extends any[]>(arr: T, size: number) {
    return arr.reduce(
      (newarr, _, i) =>
        i % size ? newarr : [...newarr, arr.slice(i, i + size)],
      []
    );
  }
  const chunkList = chunk(dataList, 200);

  // ダウンロード用のリンクを作成
  const link = document.createElement('a');
  let url: string | null = null;

  try {
    if (chunkList.length <= 1) {
      // lengthが1以下の場合、アクティベーションファイルを1つ作成
      // ACTファイルを生成
      const actFile = {
        company: actHeader.company,
        description: actHeader.description,
        actVersion: actHeader.actVersion,
        activations: chunkList[0]
      };
      // ファイル名を生成
      const fileName = 'C_' + vendorPurchaseId + '.act.dat';
      // JSON文字列に変換
      const jsonStr = JSON.stringify(actFile);
      // JSON文字列をBlobに変換
      const blob = new Blob([jsonStr], {
        type: 'application/json;charset=utf-8'
      });
      url = URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      // 作成したリンクをクリックしてダウンロードを実行
      link.click();
    } else {
      // lengthが2以上の場合、アクティベーションファイルをzipにまとめて作成
      // zipを作成
      const zip = new JSZip();

      // 200データ毎にACTファイルをzipに追加
      for (let i = 0; i < chunkList.length; i++) {
        // ACTファイルを生成
        const actFile = {
          company: actHeader.company,
          description: actHeader.description,
          actVersion: actHeader.actVersion,
          activations: chunkList[i]
        };
        // JSON文字列に変換
        const jsonStr = JSON.stringify(actFile);
        // zipファイルを作成
        zip.file(`C_${vendorPurchaseId}_${i + 1}.act.dat`, jsonStr);
      }

      // zipファイル名
      const zipFileName = 'C_' + vendorPurchaseId + '.act.zip';

      // zipをblob形式で生成
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // zipファイルをダウンロード
      url = URL.createObjectURL(zipBlob);
      link.href = url;
      link.download = zipFileName;
      document.body.appendChild(link);
      link.click();
    }
  } catch (error) {
    // Error型の場合は文字列作成、それ以外の場合は強制的に文字列化
    const message =
      error instanceof Error ? `Error: ${error.message}` : String(error);
    const errorResponse: ErrorResponse = {
      requestId: GET_DEVICES_ACTIVATION_OUTPUT_ID,
      errors: [{ code: '', message: message }]
    };
    dispatch(fetchFailure(errorResponse));
    dispatch(failedFetch(GET_DEVICES_ACTIVATION_OUTPUT_ID));
    dispatch(setIsProcessing(false));
    // エラーダイアログを表示
    dispatch(setApiErrorMessage(glossary.glx(BoardGL.DownloadFailure)));
    dispatch(setStatus(BoardStatus.DEVICE_ACTIVATE_API_ERROR_COMPLETION_DISP));
    throw error;
  } finally {
    if (link.parentNode) document.body.removeChild(link);
    // オブジェクトURLの開放(少し時間を置いてから実施)
    // タイムアウト後にurlの値が変わる可能性があるため、この時点の値を定数に退避させてから渡す
    const u = url;
    if (u) setTimeout(() => URL.revokeObjectURL(u), 100);
  }
};
